import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ErrorBoundary, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { SpacesViewModel } from 'models';

import { UitkCardAloha } from 'uitk-react-cards';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';

import { FloorplanDialog } from './internals/floorplan-dialog';
import { SpaceLayoutDescription } from './internals/space-layout-description';
import { SpaceLayoutIcons } from './internals/space-layout-icons';

interface SpacesProps {
  analytics?: AnalyticsStore;
  isDialog?: boolean;
  singleColumn?: boolean;
  spacesViewModel: SpacesViewModel;
}

export const SpacesWrapper: React.FunctionComponent<SpacesProps> = inject('analytics')(
  ({ analytics, isDialog, singleColumn, spacesViewModel }) => {
    if (!spacesViewModel) {
      return null;
    }
    const { header, spaces, floorPlan, summary } = spacesViewModel;

    const colSplit = spaces && spaces.length > 6 ? 3 : 2;

    const spaceDetails = (
      <UitkLayoutGrid columns={{ large: isDialog || singleColumn ? 1 : 3, medium: 1 }} space="six">
        <UitkLayoutGridItem>
          <div>
            {header?.text && (
              <UitkHeading size={isDialog ? 500 : 600} level={3}>
                {header.text}
              </UitkHeading>
            )}
            <UitkSpacing margin={{ blockstart: 'two', blockend: 'four' }}>
              <div>
                {summary?.map((item, index) => (
                  <UitkText size={300} className="pwa-theme--grey-700" key={`space-summary-${index}`}>
                    {item}
                  </UitkText>
                ))}
              </div>
            </UitkSpacing>
            {floorPlan && floorPlan.images.length > 0 && (
              <FloorplanDialog
                floorPlan={floorPlan}
                analytics={analytics}
                id={`space-floorplan${isDialog ? '-dialog' : ''}`}
                layerId={`space-floorplan${isDialog ? '-dialog' : ''}`}
              />
            )}
          </div>
        </UitkLayoutGridItem>
        <UitkLayoutGridItem colSpan={{ large: isDialog ? 1 : 2, medium: 1 }}>
          <UitkLayoutGrid space="two" columns={{ large: colSplit, small: 2 }} data-stid="space-details-grid">
            {spaces?.map((space, index) => (
              <UitkLayoutGridItem colSpan={1} key={`spaceInfo-${index}`}>
                <div className="all-b-margin-four">
                  {space.name && (
                    <UitkText size={500} weight="bold" className="all-b-margin-half">
                      {space.name}
                    </UitkText>
                  )}
                  {space?.icons?.map(
                    (icon, iconIndex) =>
                      icon && (
                        <SpaceLayoutIcons
                          icon={icon}
                          iconSize={(space.icons.length > 1 && UitkIconSize.SMALL) || UitkIconSize.MEDIUM}
                          name={`spaceInfo-${index}-icon-${iconIndex}`}
                        />
                      )
                  )}
                  {space?.description?.map(
                    (desc, tIndex) => desc && <SpaceLayoutDescription name={desc} key={`spaceInfo-text-${index}-${tIndex}`} />
                  )}
                </div>
              </UitkLayoutGridItem>
            ))}
          </UitkLayoutGrid>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );

    return (
      <ErrorBoundary label="hotel.information.spacelayout">
        {!isDialog && (
          <Viewport>
            <ViewSmall>
              <UitkCardAloha data-stid="space-details" className={`all-x-padding-four all-y-padding-four`}>
                {spaceDetails}
              </UitkCardAloha>
            </ViewSmall>
            <ViewMedium>
              <UitkCardAloha data-stid="space-details" className={`all-x-padding-four all-y-padding-four`}>
                {spaceDetails}
              </UitkCardAloha>
            </ViewMedium>
            <ViewLarge>
              <UitkCardAloha data-stid="space-details" className={`all-x-padding-six all-y-padding-six`}>
                {spaceDetails}
              </UitkCardAloha>
            </ViewLarge>
          </Viewport>
        )}
        {isDialog && <UitkCardAloha data-stid="space-details">{spaceDetails}</UitkCardAloha>}
      </ErrorBoundary>
    );
  }
);
